/** What Reporting shows: a calendar month, or the last 7 or 28 days against
 *  the days before them (the user's decision, 2026-11-04: 28 days is the
 *  longest window). */
export type WindowPeriod = "7d" | "28d";

export type ReportPeriod =
  | { kind: "month"; year: number; month: number }
  | { kind: "window"; period: WindowPeriod };

export const WINDOW_PERIODS: ReadonlyArray<{ id: WindowPeriod; days: number; label: string }> = [
  { id: "7d", days: 7, label: "7 days" },
  { id: "28d", days: 28, label: "28 days" },
];

const isWindowPeriod = (value: string | null): value is WindowPeriod =>
  WINDOW_PERIODS.some((p) => p.id === value);

export const windowDays = (period: WindowPeriod): number =>
  WINDOW_PERIODS.find((p) => p.id === period)?.days ?? 28;

/** `?period=28d` is a window and needs no `year` or `month`; no `period`,
 *  `period=month` or one this page does not know reads `year` and `month`,
 *  each falling back to `now`'s when missing or invalid. */
export const parsePeriod = (query: URLSearchParams, now: Date): ReportPeriod => {
  const period = query.get("period");
  if (isWindowPeriod(period)) return { kind: "window", period };
  const year = Number(query.get("year")) || now.getFullYear();
  const monthParam = Number(query.get("month"));
  const month = monthParam >= 1 && monthParam <= 12 ? monthParam : now.getMonth() + 1;
  return { kind: "month", year, month };
};

/** The page's query: the period (or the year and month), then the canvas,
 *  written by hand so the canvas reads `growth,(website/googleads)` in the
 *  address bar: URLSearchParams would escape every comma, slash and bracket.
 *  Ids are [a-z0-9], so nothing needs escaping. */
export const searchFor = (period: ReportPeriod, canvas: string | null): string => {
  const fields: Array<[string, string | null]> =
    period.kind === "window"
      ? [["period", period.period]]
      : [
          ["year", String(period.year)],
          ["month", String(period.month)],
        ];
  const parts = [...fields, ["canvas", canvas] as [string, string | null]]
    .filter((f): f is [string, string] => f[1] != null)
    .map(([k, v]) => `${k}=${v}`);
  return parts.length ? `?${parts.join("&")}` : "";
};
